import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaArrowLeft,
  FaDesktop,
  FaSpinner,
  FaSync,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import useApp from "../../hooks/useAppContext";
import api from "../../services/axios";
import Modal from "../../components/modal/modal";
import DetalhesParticipantes from "../../components/agenda/detalhes-participantes";
import { formatarDia, formatarHora } from "../../services/conversaoTimer";
import "./admPatrocinadoresList.css";
import "./adm.css";
import "./admEntregas.css";

function normalizarHora(valor) {
  if (!valor) return "";
  const texto = String(valor);
  return texto.length >= 5 ? texto.substring(0, 5) : texto;
}

function labelHorario(valor) {
  const hora = normalizarHora(valor);
  if (!hora) return "—";
  return hora.replace(":", "H");
}

function chaveSlot(dataIso, horarioInicio) {
  return `${dataIso} ${normalizarHora(horarioInicio)}`;
}

function chaveDeReuniao(reuniao) {
  return String(reuniao?.dt_hora_inicio || "").substring(0, 16);
}

function isReuniaoContabilizavel(reuniao) {
  if (!reuniao) return false;
  if (reuniao.nome === "Reunião de almoço/jantar") return false;
  return reuniao.tipo === "reuniao_negocio" || reuniao.tipo === "m_m";
}

function contagemVazia() {
  return { total: 0, entregues: 0 };
}

const POLLING_GRADE_MS = 30000;
const POLLING_MODAL_MS = 5000;

function statusVisualReuniao(item, agora) {
  const verificacao = DateTime.fromFormat(
    String(item.dt_hora_inicio || "").substring(0, 19),
    "yyyy-MM-dd HH:mm:ss"
  );
  if (item.status === "entregue") return "entregue";
  if (item.status === "no_show") return "atrasada";
  if (verificacao.isValid && agora > verificacao && item.status === "futura") {
    return "atrasada";
  }
  return item.status || "futura";
}

function labelStatusEntrega(status) {
  if (status === "entregue") return "Entregue";
  if (status === "atrasada" || status === "no_show") return "Atrasada";
  if (status === "futura") return "Agendada";
  return status || "—";
}

function obterSaidaAgenda(participante) {
  return participante?.saida_agenda || participante?.saidaAgenda || null;
}

function formatarDataHoraSaida(valor) {
  if (!valor) return "";
  const dia = formatarDia(valor);
  const hora = formatarHora(valor);
  if (!dia && !hora) return "";
  return `${dia}${hora ? ` ${hora.substring(0, 5)}` : ""}`.trim();
}

const AdmReunioes = () => {
  const navigate = useNavigate();
  const { getReunioesNegocio, getEventos, getDiasEvento } = useApp();

  const [diasEvento, setDiasEvento] = useState([]);
  const [slots, setSlots] = useState([]);
  const [dataSelecionada, setDataSelecionada] = useState("");
  const [isLoadingMeta, setIsLoadingMeta] = useState(true);
  const [isLoadingContagens, setIsLoadingContagens] = useState(false);
  const [error, setError] = useState(null);
  const [reunioes, setReunioes] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [tituloModal, setTituloModal] = useState("Entregas");
  const [slotAtivo, setSlotAtivo] = useState(null);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [modalReunioes, setModalReunioes] = useState([]);
  const [modalConteudos, setModalConteudos] = useState([]);
  const [modalErro, setModalErro] = useState(null);

  const intervalRef = useRef(null);
  const gradeIntervalRef = useRef(null);
  const slotAtivoRef = useRef(null);

  useEffect(() => {
    slotAtivoRef.current = slotAtivo;
  }, [slotAtivo]);

  const carregarContagens = useCallback(async (opts = {}) => {
    const silencioso = Boolean(opts.silencioso);
    if (!silencioso) setIsLoadingContagens(true);
    try {
      const lista = await getReunioesNegocio();
      setReunioes(
        (lista || []).filter(
          (item) =>
            isReuniaoContabilizavel(item) && Boolean(chaveDeReuniao(item))
        )
      );
    } catch (err) {
      console.error("Erro ao carregar contagem de reuniões:", err);
    } finally {
      if (!silencioso) setIsLoadingContagens(false);
    }
  }, [getReunioesNegocio]);

  const carregarMeta = useCallback(async () => {
    setIsLoadingMeta(true);
    setError(null);
    try {
      const [dadosDias, respSlots] = await Promise.all([
        getDiasEvento(),
        api.get("/slots"),
        carregarContagens(),
      ]);

      const dias = dadosDias?.dias || [];
      const listaSlots = respSlots.data?.dados || [];
      setSlots(listaSlots);

      const datasSlots = [
        ...new Set(listaSlots.map((s) => s.data).filter(Boolean)),
      ].sort();

      const diasBase =
        dias.length > 0
          ? dias
          : datasSlots.map((data) => {
              const parsed = DateTime.fromISO(data);
              return {
                data,
                dia: data.substring(8, 10),
                label: parsed.isValid ? parsed.toFormat("dd/MM") : data,
              };
            });

      // Só abas de dias que possuem slots cadastrados
      const diasComSlots = diasBase.filter((item) =>
        datasSlots.includes(item.data)
      );
      const diasResolvidos =
        diasComSlots.length > 0
          ? diasComSlots
          : datasSlots.map((data) => {
              const parsed = DateTime.fromISO(data);
              return {
                data,
                dia: data.substring(8, 10),
                label: parsed.isValid ? parsed.toFormat("dd/MM") : data,
              };
            });

      setDiasEvento(diasResolvidos);

      setDataSelecionada((atual) => {
        if (atual && diasResolvidos.some((d) => d.data === atual)) {
          return atual;
        }
        return diasResolvidos[0]?.data || "";
      });
    } catch (err) {
      console.error("Erro ao carregar dias/slots das entregas:", err);
      setError("Erro ao carregar dias e slots do evento ativo.");
    } finally {
      setIsLoadingMeta(false);
    }
  }, [carregarContagens, getDiasEvento]);

  useEffect(() => {
    carregarMeta();
  }, [carregarMeta]);

  useEffect(() => {
    if (isLoadingMeta) return undefined;

    gradeIntervalRef.current = setInterval(() => {
      carregarContagens({ silencioso: true });
    }, POLLING_GRADE_MS);

    return () => {
      if (gradeIntervalRef.current) {
        clearInterval(gradeIntervalRef.current);
        gradeIntervalRef.current = null;
      }
    };
  }, [isLoadingMeta, carregarContagens]);

  const contagensPorSlot = useMemo(() => {
    const mapa = {};
    reunioes.forEach((reuniao) => {
      const chave = chaveDeReuniao(reuniao);
      if (!chave) return;
      if (!mapa[chave]) mapa[chave] = contagemVazia();
      mapa[chave].total += 1;
      if (reuniao.status === "entregue") {
        mapa[chave].entregues += 1;
      }
    });
    return mapa;
  }, [reunioes]);

  const slotsDoDia = useMemo(() => {
    if (!dataSelecionada) return [];
    return slots
      .filter((s) => s.data === dataSelecionada)
      .slice()
      .sort((a, b) =>
        String(a.horario_inicio || "").localeCompare(
          String(b.horario_inicio || "")
        )
      );
  }, [slots, dataSelecionada]);

  const resumoDia = useMemo(() => {
    const base = slotsDoDia.reduce(
      (acc, slot) => {
        const chave = chaveSlot(slot.data, slot.horario_inicio);
        const contagem = contagensPorSlot[chave] || contagemVazia();
        acc.total += contagem.total;
        acc.entregues += contagem.entregues;
        return acc;
      },
      contagemVazia()
    );

    const agora = DateTime.now();
    const atrasadas = reunioes.filter((reuniao) => {
      const inicioSql = String(reuniao.dt_hora_inicio || "").substring(0, 19);
      if (!inicioSql.startsWith(dataSelecionada)) return false;
      if (reuniao.status === "entregue") return false;

      const inicio = DateTime.fromFormat(inicioSql, "yyyy-MM-dd HH:mm:ss");
      if (!inicio.isValid) return false;
      return agora >= inicio || reuniao.status === "no_show";
    }).length;

    return { ...base, atrasadas };
  }, [slotsDoDia, contagensPorSlot, reunioes, dataSelecionada]);

  const proximoSlot = useMemo(() => {
    const agora = DateTime.now();

    const candidatos = slots
      .map((slot) => {
        const data = slot.data;
        const hora = normalizarHora(slot.horario_inicio);
        if (!data || !hora) return null;

        const inicio = DateTime.fromFormat(
          `${data} ${hora}`,
          "yyyy-MM-dd HH:mm"
        );
        if (!inicio.isValid || inicio < agora) return null;

        return {
          ...slot,
          inicio,
          hora,
          labelData: inicio.toFormat("dd/MM"),
          labelHora: labelHorario(hora),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.inicio.toMillis() - b.inicio.toMillis());

    return candidatos[0] || null;
  }, [slots, reunioes]);

  const carregarModalSlot = useCallback(
    async (slot) => {
      if (!slot) return;

      const dataIso = slot.data;
      const hora = normalizarHora(slot.horario_inicio);
      const indexHora = chaveSlot(dataIso, hora);

      setTituloModal(`Entregas — ${labelHorario(hora)}`);
      setIsLoadingModal(true);
      setModalErro(null);

      try {
        const [auxReunioes, auxEventos] = await Promise.all([
          getReunioesNegocio(),
          getEventos(),
        ]);

        const reunioesValidas = (auxReunioes || []).filter(
          (item) =>
            isReuniaoContabilizavel(item) && Boolean(chaveDeReuniao(item))
        );
        setReunioes(reunioesValidas);

        const agora = DateTime.now();
        const reunioesSlot = reunioesValidas
          .filter((ap) => chaveDeReuniao(ap) === indexHora)
          .slice()
          .sort((a, b) => {
            const tipoA = a.tipo === "m_m" ? 1 : 0;
            const tipoB = b.tipo === "m_m" ? 1 : 0;
            if (tipoA !== tipoB) return tipoA - tipoB;

            return String(a.participantes?.[0]?.usuario?.nome || "").localeCompare(
              String(b.participantes?.[0]?.usuario?.nome || ""),
              "pt-BR",
            );
          })
          .map((item) => ({
            id: item.id,
            tipo: item.tipo,
            status: statusVisualReuniao(item, agora),
            convidado: item.participantes?.[0]?.usuario || null,
            patrocinador:
              item.participantes?.[1]?.usuario?.empresa ||
              item.participantes?.[1]?.usuario?.nome ||
              "—",
          }));

        const eventosSlot = (auxEventos || []).filter(
          (ap) =>
            String(ap.dt_hora_inicio || "").substring(0, 16) === indexHora &&
            ap.tipo !== "esportes" &&
            ap.tipo !== "esporte"
        );

        const conteudos = eventosSlot.flatMap((item) => {
          const verificacao = DateTime.fromFormat(
            String(item.dt_hora_inicio || "").substring(0, 19),
            "yyyy-MM-dd HH:mm:ss"
          );

          return (item.participantes || []).map((participante) => {
            const saidaAgenda = obterSaidaAgenda(participante);
            let status = "futura";
            if (participante?.presenca || saidaAgenda) status = "entregue";
            else if (
              verificacao.isValid &&
              agora > verificacao &&
              participante.status === 0
            ) {
              status = "atrasada";
            }

            return {
              id: `${item.id}-${participante.id}`,
              status,
              saiu: Boolean(saidaAgenda),
              eventoNome: item.nome,
              convidado: participante.usuario || null,
              saidaAgenda,
              saidaLabel: formatarDataHoraSaida(saidaAgenda),
            };
          });
        });

        setModalReunioes(reunioesSlot);
        setModalConteudos(conteudos);
      } catch (err) {
        console.error("Erro ao carregar entregas do slot:", err);
        setModalErro("Erro ao carregar reuniões deste horário.");
        setModalReunioes([]);
        setModalConteudos([]);
      } finally {
        setIsLoadingModal(false);
      }
    },
    [getEventos, getReunioesNegocio]
  );

  const limparPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const abrirSlot = async (slot) => {
    setSlotAtivo(slot);
    setShowModal(true);
    setModalReunioes([]);
    setModalConteudos([]);
    await carregarModalSlot(slot);
  };

  const fecharModal = () => {
    setShowModal(false);
    setSlotAtivo(null);
    setModalReunioes([]);
    setModalConteudos([]);
    setModalErro(null);
    limparPolling();
  };

  const resumoModal = useMemo(() => {
    const entregues = modalReunioes.filter((r) => r.status === "entregue").length;
    const atrasadas = modalReunioes.filter((r) => r.status === "atrasada").length;
    const saidas = modalConteudos.filter((c) => c.saiu).length;
    return {
      total: modalReunioes.length,
      entregues,
      atrasadas,
      conteudos: modalConteudos.length,
      saidas,
    };
  }, [modalReunioes, modalConteudos]);

  useEffect(() => {
    limparPolling();
    if (!showModal || !slotAtivo) return undefined;

    intervalRef.current = setInterval(() => {
      if (slotAtivoRef.current) {
        carregarModalSlot(slotAtivoRef.current);
      }
    }, POLLING_MODAL_MS);

    return limparPolling;
  }, [showModal, slotAtivo, carregarModalSlot, limparPolling]);

  return (
    <div className="container-adm-main">
      <Modal
        titulo={tituloModal}
        show={showModal}
        toggleModal={fecharModal}
        size="lg"
        dialogClassName="modal-entregas-detalhe"
      >
        <div className="entregas-modal">
          {isLoadingModal &&
          modalReunioes.length === 0 &&
          modalConteudos.length === 0 &&
          !modalErro ? (
            <div className="apl-loading">
              <FaSpinner className="fa-spin" size={24} aria-hidden />
              <span>Carregando entregas…</span>
            </div>
          ) : modalErro ? (
            <div className="apl-alert" role="alert">
              {modalErro}
            </div>
          ) : (
            <>
              <div className="entregas-modal-resumo" aria-label="Resumo do horário">
                <span>
                  <strong>{resumoModal.entregues}</strong>/{resumoModal.total}{" "}
                  reuniões entregues
                </span>
                <span>
                  <strong>{resumoModal.atrasadas}</strong> atrasadas
                </span>
                <span>
                  <strong>{resumoModal.conteudos}</strong> conteúdos
                </span>
                <span>
                  <strong>{resumoModal.saidas}</strong> saíram
                </span>
              </div>

              <div className="entregas-modal-scroll">
                <section className="entregas-modal-secao">
                  <header className="entregas-modal-secao-head">
                    <h2>Reuniões de negócios</h2>
                    <span>{modalReunioes.length}</span>
                  </header>

                  {modalReunioes.length === 0 ? (
                    <p className="entregas-modal-vazio">
                      Nenhuma reunião neste horário.
                    </p>
                  ) : (
                    <div className="entregas-modal-grid">
                      {modalReunioes.map((item) => (
                        <article
                          key={item.id}
                          className={[
                            "entregas-item",
                            `is-${item.status}`,
                            item.tipo === "m_m" ? "has-badge-mm" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {item.tipo === "m_m" ? (
                            <span className="entregas-item-badge entregas-item-badge--mm">
                              M&M
                            </span>
                          ) : null}
                          <span
                            className={`entregas-item-status is-${item.status}`}
                          >
                            {labelStatusEntrega(item.status)}
                          </span>
                          <DetalhesParticipantes usuario={item.convidado} />
                          <div className="entregas-item-patro">
                            {item.patrocinador}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </section>

                <section className="entregas-modal-secao">
                  <header className="entregas-modal-secao-head">
                    <h2>Salas de conteúdos</h2>
                    <span>{modalConteudos.length}</span>
                  </header>

                  {modalConteudos.length === 0 ? (
                    <p className="entregas-modal-vazio">
                      Nenhum conteúdo neste horário.
                    </p>
                  ) : (
                    <div className="entregas-modal-grid">
                      {modalConteudos.map((item) => (
                        <article
                          key={item.id}
                          className={`entregas-item is-${item.status}`}
                        >
                          <span
                            className={`entregas-item-status is-${
                              item.saiu ? "saiu" : item.status
                            }`}
                          >
                            {item.saiu
                              ? "Saiu"
                              : labelStatusEntrega(item.status)}
                          </span>
                          <DetalhesParticipantes
                            usuario={item.convidado}
                            saidaLabel={item.saidaLabel}
                          />
                          <div className="entregas-item-patro">
                            {item.eventoNome}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </>
          )}
        </div>
      </Modal>

      <div className="apl-page">
        <header className="apl-header">
          <div className="apl-header-text">
            <h1>Entregas</h1>
            <p>
              Selecione o dia do evento ativo e o horário (slot) para acompanhar
              as entregas de reuniões e conteúdos.
            </p>
          </div>

          <div className="apl-toolbar apl-toolbar--inline">
            <div className="apl-toolbar-actions">
              <Link
                to="/private/adm/entregas-monitor"
                className="apl-btn apl-btn--primary"
              >
                <FaDesktop size={14} aria-hidden />
                Monitorar em tempo real
              </Link>
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={carregarMeta}
                disabled={isLoadingMeta}
                title="Atualizar dias e slots"
              >
                <FaSync size={14} aria-hidden />
                Atualizar
              </button>
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => navigate(-1)}
                title="Voltar"
              >
                <FaArrowLeft size={14} aria-hidden />
                Voltar
              </button>
            </div>
          </div>
        </header>

        {error ? (
          <div className="apl-alert" role="alert">
            {error}
          </div>
        ) : null}

        {isLoadingMeta ? (
          <div className="apl-card">
            <div className="apl-loading">
              <FaSpinner className="fa-spin" size={28} aria-hidden />
              <span>Carregando dias e slots do evento…</span>
            </div>
          </div>
        ) : (
          <>
            {diasEvento.length > 0 ? (
              <nav className="apl-day-tabs" aria-label="Dias do evento">
                {diasEvento.map((item) => (
                  <button
                    key={item.data}
                    type="button"
                    className={`apl-day-tab${
                      dataSelecionada === item.data ? " is-active" : ""
                    }`}
                    onClick={() => setDataSelecionada(item.data)}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            ) : null}

            <section className="apl-metrics" aria-label="Resumo do dia">
              <div className="apl-metric apl-metric--alert">
                <span className="apl-metric-value">{resumoDia.atrasadas}</span>
                <span className="apl-metric-label">
                  {resumoDia.atrasadas === 1
                    ? "Reunião atrasada"
                    : "Reuniões atrasadas"}
                </span>
              </div>
              <div className="apl-metric entregas-metric--ok">
                <span className="apl-metric-value">
                  {resumoDia.entregues}/{resumoDia.total}
                </span>
                <span className="apl-metric-label">
                  {isLoadingContagens
                    ? "Atualizando contagem…"
                    : "Reuniões entregues"}
                </span>
              </div>
              <div className="apl-metric apl-metric--total">
                <span className="apl-metric-value">
                  {proximoSlot
                    ? `${proximoSlot.labelData} ${proximoSlot.labelHora}`
                    : "—"}
                </span>
                <span className="apl-metric-label">
                  {proximoSlot
                    ? "Próximo slot a iniciar"
                    : "Nenhum slot futuro"}
                </span>
              </div>
            </section>

            <section className="apl-card" aria-label="Horários do dia">
              {slotsDoDia.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">
                    Nenhum slot cadastrado para este dia
                  </p>
                  <p style={{ margin: 0 }}>
                    Cadastre slots em Agenda ou selecione outro dia do evento.
                  </p>
                </div>
              ) : (
                <div className="entregas-slots-grid">
                  {slotsDoDia.map((slot) => {
                    const hora = normalizarHora(slot.horario_inicio);
                    const fim = normalizarHora(slot.horario_fim);
                    const chave = chaveSlot(slot.data, slot.horario_inicio);
                    const contagem =
                      contagensPorSlot[chave] || contagemVazia();
                    const todasEntregues =
                      contagem.total > 0 &&
                      contagem.entregues === contagem.total;
                    const nenhumaAgendada = contagem.total === 0;

                    return (
                      <button
                        key={slot.id}
                        type="button"
                        className={`entregas-slot-btn${
                          todasEntregues ? " is-completo" : ""
                        }${nenhumaAgendada ? " is-vazio" : ""}`}
                        onClick={() => abrirSlot(slot)}
                      >
                        <span className="entregas-slot-top">
                          <span className="entregas-slot-hora">
                            {labelHorario(hora)}
                          </span>
                          <span className="entregas-slot-count">
                            <span className="entregas-slot-count-num">
                              {contagem.entregues} / {contagem.total}
                            </span>
                            <span className="entregas-slot-count-label">
                              {contagem.total === 1
                                ? "reunião entregue"
                                : "reuniões entregues"}
                            </span>
                          </span>
                        </span>
                        <span className="entregas-slot-meta">
                          {hora}
                          {fim ? ` – ${fim}` : ""}
                        </span>
                        {slot.descricao ? (
                          <span className="entregas-slot-desc">
                            {slot.descricao}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdmReunioes;
